import { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  useScrollTrigger,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/Logo';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { WhatsappIcon } from '@/components/WhatsappIcon';
import { whatsappLink } from '@/lib/whatsapp';
import { usePublicContent } from '@/features/home/use-public-content';

// Absolute, not bare hash fragments: "#leistungen" resolves against the
// current path, so it goes nowhere from /projekte or a project detail page.
const NAV = [
  { key: 'projects', href: '/projekte' },
  { key: 'services', href: '/#leistungen' },
  { key: 'process', href: '/#ablauf' },
  { key: 'advantages', href: '/#vorteile' },
  { key: 'contact', href: '/#kontakt' },
] as const;

export function PublicHeader() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { settings } = usePublicContent();
  const elevated = useScrollTrigger({ disableHysteresis: true, threshold: 8 });
  const phone = settings['company.phone'] ?? '+49 176 62655180';
  const chat = whatsappLink(settings['company.whatsapp'], t('contact.whatsappDraft'));

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          bgcolor: 'background.default',
          transition: 'box-shadow .25s ease',
          boxShadow: elevated ? '0 1px 12px rgba(0,0,0,.06)' : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ gap: 2, minHeight: { xs: 64, md: 76 } }}>
            <Box component={RouterLink} to="/" sx={{ textDecoration: 'none', color: 'inherit' }}>
              <Logo />
            </Box>
            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
              {NAV.map((item) => (
                <Button key={item.key} href={item.href} color="inherit" sx={{ fontWeight: 500 }}>
                  {t(`nav.${item.key}`)}
                </Button>
              ))}
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              {/* WhatsApp already has a floating button and a drawer entry on
                  small screens; a third copy only crowds the bar. */}
              <Tooltip title={t('contact.whatsappUs')}>
                <IconButton
                  href={chat}
                  target="_blank"
                  rel="noopener"
                  aria-label={t('contact.whatsappUs')}
                  size="small"
                  sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                >
                  <WhatsappIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <LanguageSwitcher />
              <ThemeToggle />
              <Button
                href={`tel:${phone.replace(/\s/g, '')}`}
                variant="contained"
                color="secondary"
                startIcon={<PhoneIcon />}
                sx={{ display: { xs: 'none', md: 'inline-flex' }, ml: 0.5 }}
              >
                {t('nav.request')}
              </Button>
              <IconButton
                onClick={() => setOpen(true)}
                sx={{ display: { md: 'none' } }}
                aria-label="Menu"
              >
                <MenuIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 288 }} role="presentation">
          <Stack direction="row" alignItems="center" sx={{ p: 2 }}>
            <Logo compact />
            <Box sx={{ flex: 1 }} />
            <IconButton onClick={() => setOpen(false)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <List component="nav" sx={{ py: 1 }}>
            {NAV.map((item) => (
              <ListItemButton key={item.key} href={item.href} onClick={() => setOpen(false)}>
                <ListItemText primary={t(`nav.${item.key}`)} />
              </ListItemButton>
            ))}
          </List>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<PhoneIcon />}
                href={`tel:${phone.replace(/\s/g, '')}`}
              >
                {phone}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                size="large"
                startIcon={<WhatsappIcon />}
                href={chat}
                target="_blank"
                rel="noopener"
              >
                {t('contact.whatsappUs')}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
